import AutocompleteWrapper from '@/components/AutocompleteWrapper/AutocompleteWrapper'
import type { TypeAheadConfigEndpoint } from '@/components/TypeAhead/types'
import { DEFAULT_COLLISION_AVOIDANCE } from '@/components/SearchForm/constants'
import { fireInfantErrorEvent } from '@/components/TravelerSelection/ga4'
import type { GA4PageNameType } from '@/ga4types'
import useSeti from '@/hooks/useSeti'
import logSearchFormAnalytics from '@/shared-utils/log-search-form-analytics'
import { HorizonFlyDatePicker } from '@pcln/horizon-fly-date-picker'
import { FlightSearchForm } from '@pcln/horizon-search-form'
import type { ParsedUrlQuery } from 'node:querystring'
import { useCallback, useMemo, useState } from 'react'
import { fireBundleAndSaveEvent } from '../ga4'
import type { FlightFormStateType } from '../types'
import { DEFAULT_VALUES } from './constants'
import useInitialFlightFormValues from './useInitialFlightFormValues'

type FlightSearchFormProps = {
  isRebook?: boolean
  isModify?: boolean
  onSubmit: (pageName: GA4PageNameType, data: FlightFormStateType, queryParams?: ParsedUrlQuery) => void
  pageName?: GA4PageNameType
  useCompactLayout?: boolean
}

export default function HorizonFlightForm({
  isRebook = false,
  isModify = false,
  onSubmit,
  pageName = 'homepage',
  useCompactLayout = false,
}: FlightSearchFormProps) {
  const { initialValues, query, isMobile, cguid, appName, appVersion } =
    useInitialFlightFormValues(isRebook, isModify)

  const [trackedAirports, setTrackedAirports] = useState({
    startId: initialValues.flights?.[0]?.startLocation?.id ?? '',
    startCityCode: initialValues.flights?.[0]?.startLocation?.cityCode ?? '',
    endId: initialValues.flights?.[0]?.endLocation?.id ?? '',
    endCityCode: initialValues.flights?.[0]?.endLocation?.cityCode ?? '',
  })

  const memoizedFlights = useMemo(
    () => [{
      departureAirport: { id: trackedAirports.startId, cityCode: trackedAirports.startCityCode },
      arrivalAirport: { id: trackedAirports.endId, cityCode: trackedAirports.endCityCode },
    }],
    [trackedAirports.startId, trackedAirports.startCityCode, trackedAirports.endId, trackedAirports.endCityCode],
  )

  const handleAutocompleteSelect = useCallback(
    (searchKey: string, item: { id: string; cityCode?: string } | null) => {
      if (searchKey === 'flights.0.startLocation') {
        setTrackedAirports((prev) => ({
          ...prev,
          startId: item?.id ?? '',
          startCityCode: item?.cityCode ?? '',
        }))
      } else if (searchKey === 'flights.0.endLocation') {
        setTrackedAirports((prev) => ({
          ...prev,
          endId: item?.id ?? '',
          endCityCode: item?.cityCode ?? '',
        }))
      }
    },
    [],
  )

  const removeBackground =
    useSeti({ experimentCode: 'HP_SEARCH_FORM_HORIZON_CROSS_SELL_DESKTOP' }) === 'VARIANT'

  const apolloClientInfo = useMemo(
    () => ({ clientName: appName, clientVersion: appVersion }),
    [appName, appVersion],
  )

  return (
    <FlightSearchForm
      variant={useCompactLayout ? 'compact' : 'full'}
      initialValues={initialValues}
      isMobile={isMobile}
      autoAdvance={!isMobile}
      removeBackground={removeBackground}
      isTravelerSelectionDisabled={isRebook || isModify}
      onInfantError={() => fireInfantErrorEvent(pageName, 'air')}
      onBundleChange={(tripType, checked) => {
        if (checked) {
          try {
            fireBundleAndSaveEvent(tripType)
          } catch {
            // GA4TripTypeMapper may throw on unexpected trip type combinations
          }
        }
      }}
      onSubmit={(data) => {
        if (isMobile && typeof window.PCLN?.pclnEventDispatcher?.dispatch === 'function') {
          window.PCLN.pclnEventDispatcher.dispatch('FIND_YOUR_FLIGHT')
          window.PCLN.pclnEventDispatcher.dispatch('FIND_YOUR_FLIGHT_DATA', {
            originCityName: data.flights[0]?.startLocation?.cityName,
            originAirportCode: data.flights[0]?.startLocation?.id,
            destinationCityName: data.flights[0]?.endLocation?.cityName,
            destinationCityAirportCode: data.flights[0]?.endLocation?.id,
            departureDate: data.flights[0]?.startDate,
            returnDate: data.flights[0]?.endDate,
          })
        }
        onSubmit(pageName, data as FlightFormStateType, query)
        logSearchFormAnalytics({ cguid, pageName, productType: 'flights', isMobile })
      }}
      renderAutocomplete={(props) => (
        <AutocompleteWrapper
          ref={props.inputRef}
          searchKey={props.searchKey}
          label={props.label}
          typeAheadEndpoint={props.typeAheadEndpoint as TypeAheadConfigEndpoint}
          errorMessage={props.errorMessage}
          initialSelectedItem={props.initialSelectedItem}
          onItemSelect={(item) => {
            handleAutocompleteSelect(props.searchKey, item)
            props.onItemSelect(item)
          }}
        />
      )}
      renderDatePicker={(props) => (
        <HorizonFlyDatePicker
          key={props.flightType}
          className='flight-dates'
          title={props.title}
          label={props.label}
          mode={props.mode}
          numberOfMonths={props.numberOfMonths}
          selected={props.selected}
          errorMessage={props.errorMessage}
          handleCalendarChange={props.handleCalendarChange}
          inputRef={props.inputRef}
          flightDetails={{
            cabinClass: props.cabinClass,
            tripType: props.flightType || DEFAULT_VALUES.FLIGHT_TYPE,
            flights: memoizedFlights,
          }}
          apolloClientInfo={apolloClientInfo}
          disabled={props.disabled}
          startMonth={props.startMonth}
          endMonth={props.endMonth}
          defaultMonth={props.defaultMonth}
          min={props.min}
          collisionAvoidance={DEFAULT_COLLISION_AVOIDANCE}
        />
      )}
    />
  )
}
